import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@/utils/cn';
import { useNavigate } from 'react-router-dom';
import { useCart, buildCartOrders, CartGroup, groupCartItems } from '@/Context/CartContext';
import { cartLabels, defaultPickupAddress, marketDetails, marketTiming } from '@/data/content';
import api, { getApiErrorMessage } from '@/Services/api';
import { API } from '@/Services/apiConfig';
import { formatPrice, formatGroupDate } from '@/Services/format';
import Button from '@/components/ui/Button';
import CheckoutOrderSummary from '@/components/features/CheckoutOrderSummary';
import CheckoutGroupSelector from '@/components/features/CheckoutGroupSelector';
import { trackEvent } from '@/Services/analytics';
import { useToast } from '@/Context/ToastContext';

interface GroupSelection {
    period: string;
    fulfilment: string;
    time: string;
}

interface CheckoutPeriodOption {
    key: string;
    fulfilments: string[];
    timeSlots: string[];
}

interface CheckoutGroupInfo {
    date: string;
    availablePeriods: CheckoutPeriodOption[];
    defaultPeriod: string;
    defaultFulfilment: string;
}

interface CheckoutLoadResponse {
    groups: CheckoutGroupInfo[];
}

const paymentMethods = [
    { value: 'online', label: cartLabels.payment_online, description: cartLabels.payment_online_desc },
    {
        value: 'cb_on_delivery',
        label: cartLabels.payment_cb_delivery,
        description: cartLabels.payment_cb_delivery_desc,
    },
    {
        value: 'cash_on_delivery',
        label: cartLabels.payment_cash_delivery,
        description: cartLabels.payment_cash_delivery_desc,
    },
];

// ── Component ──

const CheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const { cartItems, getCartTotal, clearCart, invalidItemIds, setInvalidItemIds } = useCart();

    // ── Form state ──
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [groupSelections, setGroupSelections] = useState<Record<string, GroupSelection>>({});
    const [paymentMethod, setPaymentMethod] = useState('online');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showToast } = useToast();
    const [successData, setSuccessData] = useState<{
        orders: { order_number: string; pickup_code: string; service_date: string; delivery_time: string }[];
    } | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [checkoutGroups, setCheckoutGroups] = useState<Record<string, CheckoutGroupInfo>>({});
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    // ── Pickup address resolution ──
const resolvePickupAddress = (date: string, period: string): string => {
    if (!period) return defaultPickupAddress;
    const match = marketTiming.find((mt) => mt.date === date && mt.period === period);
    if (match) {
        const detail = marketDetails.find((d) => d.id === match.contactId);
        if (detail) return detail.address;
    }
    return defaultPickupAddress;
};

// ── Derived data ──
    const hasDelivery = useMemo(() => {
        return Object.entries(groupSelections).some(([date, sel]) => {
            return sel.fulfilment === 'delivery';
        });
    }, [groupSelections]);

    // ── Group cart items for display ──
    const grouped: CartGroup[] = useMemo(() => {
        const storageDate = typeof window !== 'undefined' ? localStorage.getItem('selectedServiceDate') : null;
        return groupCartItems(cartItems, storageDate ?? undefined);
    }, [cartItems]);

    // ── Load backend checkout options ──
    useEffect(() => {
        if (grouped.length === 0) return;

        const abortController = new AbortController();

        const loadCheckoutOptions = async () => {
            setIsLoadingOptions(true);

            try {
                const { data } = await api.post<CheckoutLoadResponse>(API.CHECKOUT_LOAD, {
                    cartOrders: grouped.map((group) => ({
                        date: group.date ?? '',
                        period: group.items[0]?.item.period ?? null,
                        fulfilment: group.items[0]?.item.fulfilment ?? null,
                        foodItems: group.items.map(({ item }) => ({
                            id: item.id,
                            name: item.name,
                            price: item.price,
                            quantity: item.quantity,
                            menu_id: item.menu_id,
                            menu_name: item.menu_name,
                            menu_instance_id: item.menu_instance_id,
                            menu_group_name: item.menu_group_name,
                        })),
                    })),
                    signal: abortController.signal,
                });

                if (abortController.signal.aborted) return;

                const nextGroups = Object.fromEntries((data.groups ?? []).map((group) => [group.date, group]));

                setCheckoutGroups(nextGroups);
                setGroupSelections(
                    Object.fromEntries(
                        grouped.map((group) => {
                            const groupInfo = nextGroups[group.date ?? ''];
                            return [
                                group.key,
                                {
                                    period: groupInfo?.defaultPeriod ?? '',
                                    fulfilment: groupInfo?.defaultFulfilment ?? '',
                                    time: '',
                                },
                            ];
                        })
                    )
                );
            } catch (err: unknown) {
                if (!abortController.signal.aborted) {
                    showToast('error', getApiErrorMessage(err, cartLabels.error_generic));
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setIsLoadingOptions(false);
                }
            }
        };

        loadCheckoutOptions();

        return () => {
            abortController.abort();
        };
    }, [grouped]);

    // ── Validate cart items against menu ──
    useEffect(() => {
        if (cartItems.length === 0) return;

        const abortController = new AbortController();

        const validateCart = async () => {
            try {
                const cartDate =
                    cartItems.find((item) => item.date)?.date ??
                    (typeof window !== 'undefined' ? localStorage.getItem('selectedServiceDate') : null) ??
                    undefined;

                const { data } = await api.post(API.MENU, {
                    date: cartDate,
                    cartOrders: buildCartOrders(cartItems),
                    signal: abortController.signal,
                });
                if (!abortController.signal.aborted && data.invalid_cart_item_ids) {
                    setInvalidItemIds(new Set(data.invalid_cart_item_ids as number[]));
                }
            } catch {
                // silently fail
            }
        };

        validateCart();

        return () => {
            abortController.abort();
        };
    }, [cartItems, setInvalidItemIds]);

    // ── Track checkout started ──
    useEffect(() => {
        if (cartItems.length > 0) {
            trackEvent('checkout_started', {
                cart_total: getCartTotal(),
                item_count: cartItems.length,
            });
        }
    }, []); // Only on mount

    // ── Redirect if cart empty (unless success) ──
    useEffect(() => {
        if (cartItems.length === 0 && !successData) {
            navigate('/menu', { replace: true });
        }
    }, [cartItems, navigate, successData]);

    // ── Validation ──
    const validateFields = (): boolean => {
        const errs: Record<string, string> = {};
        if (!name.trim()) errs.name = cartLabels.error_name;
        if (!email.trim()) errs.email = cartLabels.error_email;
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = cartLabels.error_email;
        if (!phone.trim()) errs.phone = cartLabels.error_phone;
        if (hasDelivery && !address.trim()) errs.address = cartLabels.error_address;
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const clearFieldError = (field: string) => {
        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    // ── Submit handler ──
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateFields()) return;
        if (isLoadingOptions) return;

        // Validate each group has selections
        for (const group of grouped) {
            const sel = groupSelections[group.key];
            if (!sel?.period || !sel?.fulfilment || !sel?.time) {
                showToast(
                    'error',
                    cartLabels.error_time.replace('{date}', formatGroupDate(group.date, cartLabels.group_no_date))
                );
                setIsSubmitting(false);
                return;
            }
        }
        setIsSubmitting(true);

        try {
            const cartOrders = grouped.map((group) => {
                const sel = groupSelections[group.key];
                return {
                    date: group.date,
                    period: sel.period,
                    fulfilment: sel.fulfilment,
                    time: sel.time,
                    foodItems: group.items.map(({ item }) => ({
                        id: item.id,
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                        weight_percentage: Number(item.weight_percentage ?? 0),
                        menu_id: item.menu_id,
                        menu_name: item.menu_name,
                        menu_instance_id: item.menu_instance_id,
                        menu_group_name: item.menu_group_name,
                    })),
                };
            });

            const response = await api.post(API.CHECKOUT, {
                name,
                email,
                phone,
                address: hasDelivery ? address : null,
                payment_method: paymentMethod,
                cartOrders,
            });

            const data = response.data;

            // Online payment → redirect to Stripe
            if (data.redirect_url) {
                trackEvent('checkout_payment_redirect', { payment_method: paymentMethod });
                window.location.assign(data.redirect_url);
                return;
            }

            // On-delivery methods → show success
            clearCart();
            trackEvent('checkout_success', { payment_method: paymentMethod });
            setSuccessData({ orders: data.orders });
        } catch (err: unknown) {
            showToast('error', getApiErrorMessage(err, cartLabels.error_generic));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Don't render while redirecting (unless success) ──
    if (cartItems.length === 0 && !successData) {
        return null;
    }

    // ── Success view ──
    if (successData) {
        return (
            <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-app-surface border border-app-border rounded-2xl p-8 text-center shadow-sm">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                            className="w-8 h-8 text-green-600 dark:text-green-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-app-text mb-2">{cartLabels.success_title}</h2>
                    <p className="text-app-muted mb-6 leading-relaxed">
                        {successData.orders.length === 1 ? (
                            <>{cartLabels.success_single.replace('{number}', successData.orders[0].order_number)}</>
                        ) : (
                            <>{cartLabels.success_multiple.replace('{count}', String(successData.orders.length))}</>
                        )}
                    </p>
                    <div className="space-y-3">
                        {successData.orders.map((order) => (
                            <div key={order.order_number} className="bg-app-bg border border-app-border rounded-xl p-4">
                                <p className="text-xs font-semibold text-app-muted mb-1">
                                    Commande n°{order.order_number}
                                    {order.service_date && (
                                        <> — {new Date(order.service_date + 'T00:00:00').toLocaleDateString('fr-FR')}</>
                                    )}
                                    {order.delivery_time && <> à {order.delivery_time}</>}
                                </p>
                                <p className="text-3xl font-black text-app-primary tracking-widest">
                                    {order.pickup_code}
                                </p>
                            </div>
                        ))}
                    </div>
                    <Button variant="primary" onClick={() => navigate('/', { replace: true })} fullWidth>
                        {cartLabels.success_back}
                    </Button>
                </div>
            </div>
        );
    }

    // ── Main checkout form ──
    return (
        <div className="min-h-screen bg-app-bg pb-20">
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-black text-app-text mb-3">{cartLabels.page_title}</h1>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    {/* Two columns on desktop, stacked on mobile */}
                    <div className="lg:grid lg:grid-cols-2 lg:gap-8">
                        {/* ── Left column: Contact + Payment ── */}
                        <div className="space-y-6">
                            {/* Contact Info */}
                            <section className="bg-app-surface border border-app-border rounded-2xl p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-app-text mb-5">{cartLabels.contact_section}</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label
                                            htmlFor="checkout-name"
                                            className="block text-sm font-semibold text-app-text mb-1.5"
                                        >
                                            {cartLabels.name_label} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="checkout-name"
                                            type="text"
                                            required
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                clearFieldError('name');
                                            }}
                                            placeholder={cartLabels.name_placeholder}
                                            className={cn(
                                                'w-full px-4 py-3 rounded-xl border bg-app-bg text-app-text placeholder-app-muted focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all',
                                                fieldErrors.name ? 'border-red-500' : 'border-app-border'
                                            )}
                                        />
                                        {fieldErrors.name && (
                                            <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="checkout-email"
                                            className="block text-sm font-semibold text-app-text mb-1.5"
                                        >
                                            {cartLabels.email_label} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="checkout-email"
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                clearFieldError('email');
                                            }}
                                            placeholder={cartLabels.email_placeholder}
                                            className={cn(
                                                'w-full px-4 py-3 rounded-xl border bg-app-bg text-app-text placeholder-app-muted focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all',
                                                fieldErrors.email ? 'border-red-500' : 'border-app-border'
                                            )}
                                        />
                                        {fieldErrors.email && (
                                            <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="checkout-phone"
                                            className="block text-sm font-semibold text-app-text mb-1.5"
                                        >
                                            {cartLabels.phone_label} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="checkout-phone"
                                            type="tel"
                                            required
                                            value={phone}
                                            onChange={(e) => {
                                                setPhone(e.target.value);
                                                clearFieldError('phone');
                                            }}
                                            placeholder={cartLabels.phone_placeholder}
                                            className={cn(
                                                'w-full px-4 py-3 rounded-xl border bg-app-bg text-app-text placeholder-app-muted focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all',
                                                fieldErrors.phone ? 'border-red-500' : 'border-app-border'
                                            )}
                                        />
                                        {fieldErrors.phone && (
                                            <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
                                        )}
                                    </div>

                                    {hasDelivery && (
                                        <div>
                                            <label
                                                htmlFor="checkout-address"
                                                className="block text-sm font-semibold text-app-text mb-1.5"
                                            >
                                                {cartLabels.address_label} <span className="text-red-500">*</span>
                                            </label>
                                            <textarea
                                                id="checkout-address"
                                                required
                                                value={address}
                                                onChange={(e) => {
                                                    setAddress(e.target.value);
                                                    clearFieldError('address');
                                                }}
                                                placeholder={cartLabels.address_placeholder}
                                                rows={2}
                                                className={cn(
                                                    'w-full px-4 py-3 rounded-xl border bg-app-bg text-app-text placeholder-app-muted focus:outline-none focus:ring-2 focus:ring-app-primary/40 focus:border-app-primary transition-all',
                                                    'resize-none',
                                                    fieldErrors.address ? 'border-red-500' : 'border-app-border'
                                                )}
                                            />
                                            {fieldErrors.address && (
                                                <p className="text-red-500 text-xs mt-1">{fieldErrors.address}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Payment Method */}
                            <section className="bg-app-surface border border-app-border rounded-2xl p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-app-text mb-4">{cartLabels.payment_section}</h2>
                                <div className="space-y-3">
                                    {paymentMethods.map((method) => (
                                        <label
                                            key={method.value}
                                            className={cn(
                                                'flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all',
                                                paymentMethod === method.value
                                                    ? 'border-app-primary bg-app-primary/5'
                                                    : 'border-app-border bg-app-bg hover:border-app-primary/40'
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name="payment_method"
                                                value={method.value}
                                                checked={paymentMethod === method.value}
                                                onChange={(e) => {
                                                    setPaymentMethod(e.target.value);
                                                    trackEvent('checkout_payment_selected', { method: e.target.value });
                                                }}
                                                className="mt-0.5 accent-app-primary"
                                            />
                                            <div>
                                                <span className="block text-sm font-bold text-app-text">
                                                    {method.label}
                                                </span>
                                                <span className="block text-xs text-app-muted mt-0.5">
                                                    {method.description}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </section>
                        </div>

                        {/* ── Right column: Order Summary ── */}
                        <div className="mt-6 lg:mt-0">
                            <section className="bg-app-surface border border-app-border rounded-2xl p-6 shadow-sm lg:sticky lg:top-8">
                                <h2 className="text-lg font-bold text-app-text mb-4">{cartLabels.summary_section}</h2>

                                {grouped.length === 0 ? (
                                    <p className="text-sm text-app-muted">{cartLabels.empty_cart}</p>
                                ) : (
                                    <div className="space-y-4">
                                        {invalidItemIds.size > 0 && (
                                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                                    {cartLabels.item_unavailable_warning}
                                                </p>
                                            </div>
                                        )}
                                        {grouped.map((group) => {
                                            const sel = groupSelections[group.key] ?? {
                                                period: '',
                                                fulfilment: '',
                                                time: '',
                                            };

                                            return (
                                                <div key={group.key}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className="flex-1 h-px bg-app-border/40" />
                                                        <span className="text-xs font-bold text-app-muted uppercase tracking-wider whitespace-nowrap px-2">
                                                            {formatGroupDate(group.date, cartLabels.group_no_date)}
                                                        </span>
                                                        <div className="flex-1 h-px bg-app-border/40" />
                                                    </div>
                                                    <CheckoutOrderSummary items={group.items} />

                                                    {group.items.some(({ item }) => invalidItemIds.has(item.id)) && (
                                                        <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                                                {cartLabels.item_unavailable_warning}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Per-group selectors */}
                                                    <CheckoutGroupSelector
                                                        date={group.date ?? ''}
                                                        selection={sel}
                                                        availablePeriods={
                                                            checkoutGroups[group.date ?? '']?.availablePeriods ?? []
                                                        }
                                                        onSelectionChange={(key, selUpdate) =>
                                                            setGroupSelections((prev) => ({
                                                                ...prev,
                                                                [key]: { ...prev[key], ...selUpdate },
                                                            }))
                                                        }
                                                        groupKey={group.key}
                                                    />
                                                    {sel.fulfilment === 'pickup' && sel.period && (
                                                        <div className="mt-2 px-3 py-2 rounded-xl border border-app-border bg-app-bg">
                                                            <p className="text-xs font-semibold text-app-muted mb-0.5">
                                                                {cartLabels.fulfilment_pickup_label}
                                                            </p>
                                                            <p className="text-sm text-app-text">
                                                                {resolvePickupAddress(group.date ?? '', sel.period)}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="flex justify-between items-center mt-4 pt-4 border-t border-app-border">
                                    <span className="text-app-muted font-medium">{cartLabels.total_label}</span>
                                    <span className="text-xl font-black text-app-text">
                                        {formatPrice(getCartTotal())}
                                    </span>
                                </div>
                            </section>
                        </div>
                    </div>

                    {/* Submit button */}
                    <div className="mt-6 max-w-xl mx-auto">
                        <Button
                            type="submit"
                            variant="primary"
                            fullWidth
                            size="lg"
                            disabled={isSubmitting || isLoadingOptions}
                        >
                            {isLoadingOptions
                                ? cartLabels.submitting_label
                                : isSubmitting
                                  ? cartLabels.submitting_label
                                  : cartLabels.submit_label}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CheckoutPage;
